import { useMemo } from 'react';
import { Form, Row, Col, Button, Typography, Tooltip } from 'antd';
import {
  PlusOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { PostFieldRecursive } from './PostFieldRecursive';
import { FieldSectionWrapper } from './FieldSectionWrapper';
import { ServerErrorIndicator } from './ServerErrorIndicator';

import { useGetWalmartFormItemError } from '../hooks';
import { red } from '@ant-design/colors';
import { makeRules } from './utils';

const { Text } = Typography;

const PostFieldList = (props) => {
  const {
    label,
    items,
    minItems,
    fullKey,
    isRequiredProperty,
    productId,
    serverError,
    description,
    isRivirMappingField,
    rivirMappingField,
  } = props;

  const { childProperties: childPropertiesOfItem } = items;

  const errorShowType = serverError ? 'showServerError' : 'showAntdError';

  return (
    <div style={{ marginBottom: 16 }}>
      <Form.List
        name={fullKey?.split('.')}
        rules={makeRules({
          isRequiredProperty,
          ruleType: 'formList',
          minItems,
        })}
      >
        {(fields, { add, remove }, formListParams) => {
          const errors = formListParams?.errors;

          return (
            <div>
              {!childPropertiesOfItem && (
                <Row>
                  <Col xs={24}>
                    <AddListItemButton
                      label={label}
                      listType='singleFieldList'
                      add={add}
                      description={description}
                      isRequiredProperty={isRequiredProperty}
                      isRivirMappingField={isRivirMappingField}
                      rivirMappingField={rivirMappingField}
                    />
                  </Col>
                </Row>
              )}

              {childPropertiesOfItem ? (
                <Row>
                  <Col xs={24}>
                    <MultipleFieldList
                      fields={fields}
                      label={label}
                      items={items}
                      fullKey={fullKey}
                      remove={remove}
                      productId={productId}
                    />
                  </Col>
                </Row>
              ) : (
                <div>
                  <Row>
                    <Col style={{ flex: 1, overflow: 'hidden' }} offset={12}>
                      <SingleFieldList
                        fields={fields}
                        minItems={minItems}
                        items={items}
                        fullKey={fullKey}
                        remove={remove}
                        productId={productId}
                      />
                    </Col>
                    <Col flex='30px' />
                  </Row>
                  {errorShowType === 'showServerError' && (
                    <Row>
                      <Col xs={12} offset={12}>
                        {serverError && (
                          <Text type='danger'>{serverError?.error}</Text>
                        )}
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {!!childPropertiesOfItem && (
                <>
                  <Row>
                    <Col flex='auto'>
                      <AddListItemButton
                        label={label}
                        listType='multipleFieldList'
                        add={add}
                        isRequiredProperty={isRequiredProperty}
                        isRivirMappingField={isRivirMappingField}
                        rivirMappingField={rivirMappingField}
                      />
                    </Col>
                    <Col flex='30px' />
                  </Row>
                  {errorShowType === 'showServerError' && (
                    <Row>
                      <Col xs={12} offset={12}>
                        {serverError && (
                          <Text type='danger'>{serverError?.error}</Text>
                        )}
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {errorShowType === 'showAntdError' && errors.length > 0 ? (
                <Row style={{ width: '100%' }}>
                  <Col offset={12} span={12}>
                    <Text type='danger'>{errors[0]}</Text>
                  </Col>
                </Row>
              ) : null}
            </div>
          );
        }}
      </Form.List>
    </div>
  );
};

//*List of group fields
const MultipleFieldList = ({
  fields,
  label,
  items,
  remove,
  productId,
  fullKey,
}) => {
  const { childProperties: childPropertiesOfItem } = items;

  return (
    <>
      {fields?.map((fieldItem, idx) => {
        if (!childPropertiesOfItem) return null;
        const errorKey = `${fullKey}[${fieldItem?.name}]`;
        return (
          <FieldGroup
            key={fieldItem.key}
            childProperties={childPropertiesOfItem}
            fieldName={label}
            fieldItem={fieldItem}
            index={idx}
            remove={remove}
            productId={productId}
            errorKey={errorKey}
          />
        );
      })}
    </>
  );
};

//* group field
const FieldGroup = ({
  childProperties,
  fieldName,
  index,
  fieldItem,
  remove,
  productId,
  errorKey,
}) => {
  const title = fieldName ? `${fieldName} [${index + 1}]` : null;

  return (
    <Row>
      <Col flex={1} style={{ paddingLeft: 30 }}>
        <FieldSectionWrapper title={title}>
          {childProperties?.map((childPropertyItem) => {
            return (
              <PostFieldRecursive
                {...childPropertyItem}
                name={[fieldItem?.name, childPropertyItem?.key]}
                productId={productId}
                errorKey={`${errorKey}.${childPropertyItem?.key}`}
              />
            );
          })}
        </FieldSectionWrapper>
      </Col>
      <Col>
        <div style={{ top: 12, right: 19, position: 'relative' }}>
          <RemoveListItemButton remove={remove} field={fieldItem} />
        </div>
      </Col>
    </Row>
  );
};

//* list of single field
const SingleFieldList = ({ fields, items, fullKey, remove, productId }) => {
  if (!fields?.length) return null;

  const { type: typeOfItem, enums: enumsOfItem, format: formatOfItem } = items;

  return (
    <FieldSectionWrapper style={{ padding: 0, marginBottom: 0 }}>
      {fields?.map((fieldItem, idx) => {
        const errorKey = `${fullKey}[${fieldItem?.name}]`;

        return (
          <SingleFieldItemWrapper
            key={fieldItem.key}
            errorKey={errorKey}
            productId={productId}
          >
            <SingleFieldItem
              fieldItem={fieldItem}
              typeOfItem={typeOfItem}
              enumsOfItem={enumsOfItem}
              fullKey={fullKey}
              remove={remove}
              productId={productId}
              errorKey={errorKey}
              format={formatOfItem}
            />
          </SingleFieldItemWrapper>
        );
      })}
    </FieldSectionWrapper>
  );
};

const SingleFieldItem = ({
  fieldItem,
  typeOfItem,
  enumsOfItem,
  fullKey,
  remove,
  productId,
  errorKey,
  format,
}) => {
  return (
    <Row>
      <Col style={{ flex: 1, overflow: 'hidden' }}>
        <PostFieldRecursive
          className='post-product-form-list__form-item--single-field'
          {...fieldItem}
          wrapperCol={{ span: 24 }}
          fieldListItemProps={fieldItem}
          type={typeOfItem}
          enums={enumsOfItem}
          fullKey={fullKey}
          showServerError={false}
          errorKey={errorKey}
          productId={productId}
          name={fieldItem?.name}
          format={format}
        />
      </Col>
      <Col>
        <RemoveListItemButton remove={remove} field={fieldItem} />
      </Col>
    </Row>
  );
};

const SingleFieldItemWrapper = (props) => {
  const { children, productId, errorKey } = props;

  const formItemServerError = useGetWalmartFormItemError({
    productId,
    errorKey: errorKey,
  });

  return (
    <Row>
      <Col style={{ flex: 1, overflow: 'hidden' }}>{children}</Col>
      <Col style={{ paddingTop: 2, marginLeft: 4 }}>
        <ServerErrorIndicator serverError={formItemServerError} />
      </Col>
    </Row>
  );
};

export const RemoveListItemButton = ({ remove, field }) => {
  return (
    <Button
      type='danger'
      onClick={() => {
        remove(field.name);
      }}
      icon={<CloseCircleOutlined />}
      style={{
        marginLeft: 4,
        marginTop: 6,
        borderColor: '#ff4d4f',
        backgroundColor: red[6],
        borderRadius: 4,
      }}
    />
  );
};

const AddListItemButton = ({
  label,
  add,
  listType = ['singleFieldList', 'multiFieldList'][0],
  isRequiredProperty,
  description,
  isRivirMappingField,
  rivirMappingField,
}) => {
  const formItemConfig = useMemo(() => {
    switch (listType) {
      case 'singleFieldList':
        return {
          style: { marginBottom: 4 },
          labelCol: { span: 12 },
        };
      case 'multipleFieldList':
        return {
          style: { paddingTop: 10, paddingLeft: 30, marginBottom: 4 },
          labelCol: { span: 12 },
        };
      default:
        return {};
    }
  }, [listType]);

  return (
    <Col xs={24}>
      <Form.Item
        className={
          isRequiredProperty
            ? 'post-product-form-list__form-item--required'
            : ''
        }
        label={
          <div>
            <Tooltip title={label}>
              <Text ellipsis strong>
                {label}
              </Text>
            </Tooltip>
            {isRivirMappingField && (
              <Tooltip
                title={
                  rivirMappingField
                    ? `Auto populate from RIVIR: ${rivirMappingField}`
                    : ''
                }
                color='#238dd9'
              >
                <InfoCircleOutlined style={{ margin: '0px 4px' }} />
              </Tooltip>
            )}
            {description && (
              <Tooltip title={description} color='#238dd9'>
                <QuestionCircleOutlined style={{ margin: '0px 4px' }} />
              </Tooltip>
            )}
          </div>
        }
        {...formItemConfig}
      >
        <Button
          type='default'
          onClick={() => add()}
          icon={<PlusOutlined />}
          style={{ borderRadius: 4 }}
        >
          Add Item
        </Button>
      </Form.Item>
    </Col>
  );
};

export { PostFieldList };
